// "Will this device receive a command right now?" — asked of the device itself.
//
// WHY NOT THE STATUS TABLE. GetDeviceStatusbyUniqueNumber reports when a device
// last wrote a row, and that answer is only as good as the server's bookkeeping
// and the server's clock. Verified against the live broker on 2026-07-29:
//
//     884811   status row stamped a few minutes ago  → answered in <1s
//     7391679  status row months old                 → silence
//     1134667  status row months old                 → silence
//
// so the table is not WRONG, but it is second-hand, and it is stamped in the
// server's local time while labelled UTC (see deviceStatus.js), which puts every
// row five and a half hours in the future. A liveness rule built on comparing
// that against the phone's clock is measuring the timezone gap as much as the
// device.
//
// An MQTT round-trip has none of those problems. It asks the actual question —
// can I reach this device — over the actual channel the command will use, and it
// needs no clock at all on either side.
//
// THE PROBE IS `getallwifi`, chosen because it is the only command in the
// vocabulary that is unambiguously a READ. allon/alloff switch the load, s05's
// meaning is not documented anywhere we can check, and updatecode reflashes.
// A device that is powered and connected replies on its /client topic; one that
// is not, says nothing. The app already publishes this whenever a Wi-Fi menu is
// opened, so it is a well-trodden path, not a new demand on the firmware.

import {
  connectMqtt, subscribeTopic, unsubscribeTopic, addMessageListener,
  publishMessage, waitForConnect,
} from '../mqtt/mqttClient';
import { CMD_GET_ALL_WIFI } from './commands';

// How long to wait for replies. The verified round-trip was well under a second
// on a healthy device; the rest is headroom for a busy broker and a device
// that is mid-something.
export const PROBE_WINDOW_MS = 3500;

// Gap between publishes when probing many devices. Each reply also makes the
// server rewrite that device's stored network list, so a location's worth of
// devices is spread out rather than fired as one burst.
export const PROBE_STEP_MS = 40;

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/**
 * Ask a set of devices whether they are there.
 *
 * Returns { live: Set<string>, reachedBroker: boolean }. `reachedBroker` is the
 * part callers must not ignore: if the broker could not be reached, NOTHING
 * answered, and reporting every device as offline would be an artefact of our
 * own connection rather than a fact about the hardware.
 */
export async function probeLiveness(uniqueNumbers, windowMs = PROBE_WINDOW_MS) {
  const list = [...new Set((uniqueNumbers || []).map(String).filter(Boolean))];
  const live = new Set();
  if (!list.length) return { live, reachedBroker: true };

  connectMqtt();
  const reachedBroker = await waitForConnect();
  if (!reachedBroker) return { live, reachedBroker: false };

  const wanted = new Set(list);
  // Listen before publishing — see waitForConnect's note on why the order here
  // is not cosmetic.
  const detach = addMessageListener((topic) => {
    const t = String(topic);
    if (!t.endsWith('/client')) return;      // our own publish echoes on /device
    const uno = t.slice(0, t.indexOf('/'));
    if (wanted.has(uno)) live.add(uno);
  });

  list.forEach((uno) => subscribeTopic(`${uno}/client`));

  for (let i = 0; i < list.length; i += 1) {
    publishMessage(`${list[i]}/device`, CMD_GET_ALL_WIFI);
    // eslint-disable-next-line no-await-in-loop
    if (i < list.length - 1) await sleep(PROBE_STEP_MS);
  }

  await sleep(windowMs);
  detach();
  list.forEach((uno) => unsubscribeTopic(`${uno}/client`));

  return { live, reachedBroker: true };
}
